import { useState, type ReactNode } from "react";
import { Check, ChevronDown } from "lucide-react";
import {
  SUPPORTED_CURRENCIES,
  type SupportedCurrencyCode,
} from "@repo/api-schema";

// Code -> entry lookup, built once at module load. Avoids the O(n) `find`
// scan on every render of every combobox instance.
const CURRENCY_BY_CODE: ReadonlyMap<
  SupportedCurrencyCode,
  (typeof SUPPORTED_CURRENCIES)[number]
> = new Map(SUPPORTED_CURRENCIES.map((c) => [c.code, c]));
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { cn } from "../lib/utils";

interface CurrencyComboboxProps {
  id?: string;
  value: SupportedCurrencyCode;
  onChange: (code: SupportedCurrencyCode) => void;
  disabled?: boolean;
}

export function CurrencyCombobox({
  id,
  value,
  onChange,
  disabled,
}: CurrencyComboboxProps): ReactNode {
  const [open, setOpen] = useState(false);
  const selected = CURRENCY_BY_CODE.get(value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        id={id}
        type="button"
        role="combobox"
        aria-expanded={open}
        disabled={disabled}
        className={cn(
          "flex h-11 w-full cursor-pointer items-center justify-between rounded-sm bg-surface-container-low px-4 text-body-md text-on-surface transition-colors hover:bg-surface-container focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50",
        )}
      >
        <span className="flex items-center gap-2 truncate">
          {selected ? (
            <>
              <span className="font-medium">{selected.code}</span>
              <span className="truncate text-on-surface-variant">
                {selected.name}
              </span>
            </>
          ) : (
            <span className="text-on-surface-variant">Select currency…</span>
          )}
        </span>
        <ChevronDown className="ml-2 size-4 shrink-0 text-on-surface-variant" />
      </PopoverTrigger>
      <PopoverContent className="p-0">
        <Command
          filter={(itemValue, search) => {
            const needle = search.trim().toLowerCase();
            if (!needle) return 1;
            return itemValue.toLowerCase().includes(needle) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="Search currency…" />
          <CommandList>
            <CommandEmpty>No currency found.</CommandEmpty>
            <CommandGroup>
              {SUPPORTED_CURRENCIES.map((c) => (
                <CommandItem
                  key={c.code}
                  value={`${c.code} ${c.name}`}
                  onSelect={() => {
                    onChange(c.code);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "size-4",
                      value === c.code ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <span className="font-medium">{c.code}</span>
                  <span className="text-on-surface-variant">{c.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
